'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Globe,
  Activity,
  Eye,
  Compass,
  TrendingUp,
  BarChart3,
  Building2,
  ShoppingCart,
  Cpu,
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Shield,
  MessageSquare,
  Pin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUser } from '@/context/AuthContext';
import { useOrg } from '@/components/TivionOrgContext';

/* ─── Mega Menu Data ─── */

const megaMenuData = {
  product: {
    label: 'Platform',
    columns: [
      {
        links: [
          {
            icon: Globe,
            title: 'Discovery Hub',
            desc: 'Identify emerging competitors before they hit the Top 100.',
            href: '/platform/discovery-hub',
            previewId: 'discovery',
          },
          {
            icon: Activity,
            title: 'Pulse Feed',
            desc: 'Your real-time stream of market volatility.',
            href: '/platform/pulse-feed',
            previewId: 'pulse',
            live: true,
          },
          {
            icon: Eye,
            title: 'Visual Regression',
            desc: 'Pixel-perfect tracking for pricing and CTAs.',
            href: '/platform/visual-regression',
            previewId: 'visual',
          },
        ],
      },
      {
        links: [
          {
            icon: Compass,
            title: 'Keyword Landscape',
            desc: 'Navigate your keyword landscape with precision.',
            href: '/platform/keyword-battlefield',
            previewId: 'keyword',
          },
          {
            icon: TrendingUp,
            title: 'Rankings',
            desc: 'Track SERP positions across every competitor.',
            href: '/platform/rankings',
            previewId: 'rankings',
          },
          {
            icon: BarChart3,
            title: 'GA4 Correlation',
            desc: 'Overlay traffic data onto ranking history.',
            href: '/platform/ga4-correlation',
            previewId: 'ga4',
          },
        ],
      },
      {
        links: [
          {
            icon: Users,
            title: 'Team Workspaces',
            desc: 'RBAC roles, departments, and seat management.',
            href: '/platform/team-workspaces',
            previewId: 'teamWorkspaces',
          },
          {
            icon: Shield,
            title: 'Client Portal',
            desc: 'Secure, read-only intelligence sharing.',
            href: '/platform/client-portal',
            previewId: 'clientPortal',
          },
          {
            icon: MessageSquare,
            title: 'Pulse Comments',
            desc: '@mention teammates on market signals.',
            href: '/platform/pulse-comments',
            previewId: 'pulseComments',
            live: true,
          },
        ],
      },
    ],
  },
  solutions: {
    label: 'Solutions',
    columns: [
      {
        links: [
          {
            icon: Building2,
            title: 'SEO Agencies',
            desc: 'Manage multi-site portfolios with automated reporting.',
            href: '/solutions/agencies',
            previewId: 'agency',
          },
          {
            icon: ShoppingCart,
            title: 'E-commerce',
            desc: 'Monitor inventory shifts and competitor pricing dynamics.',
            href: '/solutions/ecommerce',
            previewId: 'ecommerce',
          },
          {
            icon: Cpu,
            title: 'Product Teams',
            desc: 'Track rival tech-stacks and feature release cycles.',
            href: '/solutions/product-teams',
            previewId: 'product',
          },
          {
            icon: Users,
            title: 'Enterprise Teams',
            desc: 'Organizational intelligence for 50+ seat teams.',
            href: '/solutions/enterprise',
            previewId: 'enterprise',
          },
        ],
      },
    ],
  },
};

/* ─── Live Preview Panels ─── */

/* ─── Favicon (colored initial) ─── */

function Favicon({ name, color }) {
  return (
    <div className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold shrink-0 ${color}`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

/* ─── Sparkline (tiny SVG chart) ─── */

function Sparkline({ points, color = 'text-shield-cyan/60', className = '' }) {
  return (
    <div className={`h-4 w-12 ${className}`}>
      <svg viewBox="0 0 48 16" className="w-full h-full" preserveAspectRatio="none">
        <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" className={color} />
      </svg>
    </div>
  );
}

const previews = {
  discovery: {
    title: 'Competitor Discovery',
    content: (
      <div className="space-y-1.5">
        {[
          { name: 'NexusPay', domain: 'nexuspay.io', rank: '#87', color: 'bg-red-500/10 text-red-400', severity: 'bg-red-400', trend: '0,14 8,12 16,10 24,8 32,6 40,4 48,2' },
          { name: 'CloudFlow', domain: 'cloudflow.ai', rank: '#142', color: 'bg-tivion-amber/10 text-tivion-amber', severity: 'bg-tivion-amber', trend: '0,12 8,10 16,11 24,8 32,7 40,5 48,4' },
          { name: 'Streamline', domain: 'streamline.ai', rank: '#201', color: 'bg-emerald-500/10 text-emerald-400', severity: 'bg-emerald-400', trend: '0,14 8,13 16,12 24,11 32,10 40,9 48,8' },
        ].map((d) => (
          <div key={d.name} className="flex items-center gap-2 p-1.5 rounded-lg bg-background/50 border border-border/30">
            <Favicon name={d.name} color={d.color} />
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-semibold text-foreground block leading-tight">{d.name}</span>
              <span className="text-[8px] text-muted-foreground">{d.domain}</span>
            </div>
            <Sparkline points={d.trend} color={d.severity === 'bg-red-400' ? 'text-red-400/60' : d.severity === 'bg-tivion-amber' ? 'text-tivion-amber/60' : 'text-emerald-400/60'} />
            <span className="text-[9px] text-muted-foreground font-mono">{d.rank}</span>
          </div>
        ))}
      </div>
    ),
  },
  pulse: {
    title: 'Pulse Feed',
    content: (
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5 mb-1.5">
          <div className="relative">
            <div className="w-1.5 h-1.5 rounded-full bg-shield-cyan" />
            <div className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-shield-cyan animate-ping opacity-40" />
          </div>
          <span className="text-[9px] text-shield-cyan font-semibold uppercase tracking-wider">Live</span>
        </div>
        {[
          { co: 'NexusPay', signal: 'Pricing Pivot', badge: 'bg-violet-500/10 text-violet-400 border-violet-500/20', favicon: 'bg-violet-500/10 text-violet-400' },
          { co: 'CloudFlow', signal: 'Tech Stack', badge: 'bg-blue-500/10 text-blue-400 border-blue-500/20', favicon: 'bg-blue-500/10 text-blue-400' },
        ].map((e) => (
          <div key={e.co} className="flex items-center gap-2 p-1.5 rounded-lg bg-background/50 border border-border/30">
            <Favicon name={e.co} color={e.favicon} />
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-semibold text-foreground">{e.co}</span>
              <span className={`ml-1.5 text-[8px] px-1 py-0.5 rounded border ${e.badge}`}>{e.signal}</span>
            </div>
            <div className="w-2 h-2 rounded-full bg-shield-cyan shrink-0" />
          </div>
        ))}
      </div>
    ),
  },
  visual: {
    title: 'Visual Regression',
    content: (
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-1">
          <Favicon name="NexusPay" color="bg-violet-500/10 text-violet-400" />
          <span className="text-[10px] font-semibold text-foreground">NexusPay</span>
        </div>
        <div className="flex rounded-lg overflow-hidden border border-border/30">
          <div className="flex-1 bg-background/50 p-2.5 text-center">
            <span className="text-[8px] text-muted-foreground uppercase">Before</span>
            <div className="text-sm font-bold text-foreground mt-0.5">$49</div>
          </div>
          <div className="w-px bg-shield-cyan/40" />
          <div className="flex-1 bg-shield-cyan/5 p-2.5 text-center">
            <span className="text-[8px] text-shield-cyan uppercase">After</span>
            <div className="text-sm font-bold text-shield-cyan mt-0.5">$39</div>
          </div>
        </div>
        <div className="text-[9px] text-tivion-amber">Pricing change — High Impact</div>
      </div>
    ),
  },
  keyword: {
    title: 'Keyword Landscape',
    content: (
      <div className="space-y-1.5">
        {[
          { kw: 'fintech pricing', you: 3, comp: 7, up: true, trend: '0,14 8,12 16,10 24,8 32,6 40,4 48,3' },
          { kw: 'ai workflow', you: 1, comp: 4, up: true, trend: '0,12 8,10 16,8 24,6 32,4 40,3 48,2' },
        ].map((r) => (
          <div key={r.kw} className="flex items-center gap-2 p-1.5 rounded-lg bg-background/50 border border-border/30">
            <div className="min-w-0 flex-1">
              <span className="text-[10px] text-foreground font-medium">&quot;{r.kw}&quot;</span>
            </div>
            <Sparkline points={r.trend} color="text-emerald-400/60" />
            <div className="flex items-center gap-0.5">
              <span className="text-[10px] text-shield-cyan font-bold">#{r.you}</span>
              {r.up ? (
                <ArrowUpRight className="h-3 w-3 text-emerald-400" />
              ) : (
                <ArrowDownRight className="h-3 w-3 text-red-400" />
              )}
            </div>
          </div>
        ))}
        <div className="text-[9px] text-shield-cyan mt-1">NexusPay +4 vs Vertex Bank</div>
      </div>
    ),
  },
  rankings: {
    title: 'Rankings',
    content: (
      <div className="space-y-2">
        <div className="h-10 rounded bg-background/30 overflow-hidden">
          <svg viewBox="0 0 120 30" className="w-full h-full" preserveAspectRatio="none">
            <polyline points="0,25 20,22 40,18 60,15 80,12 100,8 120,5" fill="none" stroke="currentColor" strokeWidth="2" className="text-shield-cyan/60" />
          </svg>
        </div>
        <div className="flex justify-between text-[9px] text-muted-foreground">
          <span>30d ago</span>
          <span className="text-emerald-400 font-medium">+12 avg. positions</span>
        </div>
      </div>
    ),
  },
  ga4: {
    title: 'GA4 Correlation',
    content: (
      <div className="space-y-2">
        <div className="h-10 rounded bg-background/30 overflow-hidden">
          <svg viewBox="0 0 120 30" className="w-full h-full" preserveAspectRatio="none">
            <polyline points="0,20 20,18 40,22 60,15 80,10 100,12 120,8" fill="none" stroke="currentColor" strokeWidth="2" className="text-shield-cyan/60" />
            <polyline points="0,25 20,23 40,20 60,18 80,14 100,16 120,11" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" className="text-tivion-amber/40" />
          </svg>
        </div>
        <div className="flex gap-3 text-[9px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-shield-cyan" /> Rankings</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-tivion-amber" /> Traffic</span>
        </div>
      </div>
    ),
  },
  agency: {
    title: 'Multi-Site Portfolio',
    content: (
      <div className="space-y-1.5">
        {[
          { name: 'Alpha Co', domain: 'client-alpha.com', signals: 24, color: 'bg-emerald-500/10 text-emerald-400', trend: '0,10 8,8 16,6 24,5 32,4 40,3 48,2' },
          { name: 'Brand Beta', domain: 'brand-beta.io', signals: 18, color: 'bg-shield-cyan/10 text-shield-cyan', trend: '0,12 8,11 16,10 24,8 32,7 40,6 48,5' },
          { name: 'Shop Gamma', domain: 'shop-gamma.co', signals: 12, color: 'bg-blue-500/10 text-blue-400', trend: '0,14 8,13 16,12 24,11 32,10 40,9 48,8' },
        ].map((d) => (
          <div key={d.name} className="flex items-center gap-2 p-1.5 rounded-lg bg-background/50 border border-border/30">
            <Favicon name={d.name} color={d.color} />
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-semibold text-foreground block leading-tight">{d.name}</span>
              <span className="text-[8px] text-muted-foreground">{d.domain}</span>
            </div>
            <Sparkline points={d.trend} color="text-shield-cyan/50" />
            <span className="text-[9px] text-muted-foreground">{d.signals}</span>
          </div>
        ))}
      </div>
    ),
  },
  ecommerce: {
    title: 'Pricing Dynamics Monitor',
    content: (
      <div className="space-y-2">
        <div className="flex rounded-lg overflow-hidden border border-border/30">
          <div className="flex-1 bg-background/50 p-2 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Favicon name="SwiftCart" color="bg-red-500/10 text-red-400" />
              <span className="text-[8px] text-muted-foreground">SwiftCart</span>
            </div>
            <div className="text-xs font-bold text-red-400">30% OFF</div>
          </div>
          <div className="w-px bg-border/30" />
          <div className="flex-1 bg-background/50 p-2 text-center">
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <Favicon name="Luna" color="bg-emerald-500/10 text-emerald-400" />
              <span className="text-[8px] text-muted-foreground">Luna Goods</span>
            </div>
            <div className="text-xs font-bold text-foreground">Full Price</div>
          </div>
        </div>
        <div className="text-[9px] text-tivion-amber">Flash sale detected — respond now</div>
      </div>
    ),
  },
  product: {
    title: 'Tech Stack Monitor',
    content: (
      <div className="space-y-2">
        <div className="flex items-center gap-2 mb-1">
          <Favicon name="Vertex" color="bg-blue-500/10 text-blue-400" />
          <span className="text-[10px] font-semibold text-foreground">Vertex Bank</span>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">+ Next.js</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">+ Segment</span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">- jQuery</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">- Meta Pixel</span>
          </div>
        </div>
        <div className="text-[9px] text-shield-cyan">Tech migration detected</div>
      </div>
    ),
  },
  teamWorkspaces: {
    title: 'Team Overview',
    content: (
      <div className="space-y-1.5">
        {[
          { name: 'Alex Chen', role: 'Admin', color: 'bg-shield-cyan/10 text-shield-cyan', badge: 'bg-shield-cyan/10 text-shield-cyan border-shield-cyan/20' },
          { name: 'Sarah Kim', role: 'Member', color: 'bg-emerald-500/10 text-emerald-400', badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
          { name: 'Client: Luna', role: 'Client', color: 'bg-tivion-amber/10 text-tivion-amber', badge: 'bg-tivion-amber/10 text-tivion-amber border-tivion-amber/20' },
        ].map((m) => (
          <div key={m.name} className="flex items-center gap-2 p-1.5 rounded-lg bg-background/50 border border-border/30">
            <Favicon name={m.name} color={m.color} />
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-semibold text-foreground block leading-tight">{m.name}</span>
            </div>
            <span className={`text-[8px] px-1 py-0.5 rounded border ${m.badge}`}>{m.role}</span>
          </div>
        ))}
        <div className="text-[9px] text-muted-foreground mt-1">8 of 10 seats used</div>
      </div>
    ),
  },
  clientPortal: {
    title: 'Client Portal',
    content: (
      <div className="space-y-2">
        <div className="p-2 rounded-lg bg-shield-cyan/5 border border-shield-cyan/20">
          <div className="flex items-center gap-1.5 mb-1.5">
            <div className="w-4 h-4 rounded bg-shield-cyan/20 flex items-center justify-center">
              <span className="text-[7px] font-bold text-shield-cyan">A</span>
            </div>
            <span className="text-[9px] font-semibold text-foreground">Acme Agency</span>
          </div>
          <div className="text-[8px] text-muted-foreground">Client portal</div>
        </div>
        {[
          { signal: 'SERP Movement', co: 'Luna Goods', severity: 'high' },
          { signal: 'Pricing Pivot', co: 'SwiftCart', severity: 'medium' },
        ].map((s) => (
          <div key={s.signal} className="flex items-center gap-2 p-1.5 rounded-lg bg-background/50 border border-border/30">
            <Favicon name={s.co} color="bg-shield-cyan/10 text-shield-cyan" />
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-semibold text-foreground">{s.co}</span>
              <span className="text-[8px] text-muted-foreground block">{s.signal}</span>
            </div>
            <div className={`w-2 h-2 rounded-full shrink-0 ${s.severity === 'high' ? 'bg-red-400' : 'bg-tivion-amber'}`} />
          </div>
        ))}
      </div>
    ),
  },
  pulseComments: {
    title: 'Signal Discussion',
    content: (
      <div className="space-y-2">
        <div className="p-1.5 rounded-lg bg-background/50 border border-border/30">
          <div className="flex items-center gap-1.5 mb-1">
            <Favicon name="NexusPay" color="bg-violet-500/10 text-violet-400" />
            <span className="text-[10px] font-semibold text-foreground">NexusPay</span>
            <span className="text-[8px] px-1 py-0.5 rounded border bg-violet-500/10 text-violet-400 border-violet-500/20">Pricing</span>
          </div>
          <div className="space-y-1.5 pl-3 border-l border-shield-cyan/20">
            <div className="p-1.5 rounded bg-background/50">
              <div className="flex items-center gap-1 mb-0.5">
                <Favicon name="Marcus" color="bg-blue-500/10 text-blue-400" />
                <span className="text-[8px] text-foreground font-medium">Marcus</span>
              </div>
              <p className="text-[8px] text-muted-foreground"><span className="text-shield-cyan">@Sarah</span>, should we match?</p>
            </div>
            <div className="p-1.5 rounded bg-shield-cyan/5 border border-shield-cyan/10">
              <div className="flex items-center gap-1 mb-0.5">
                <Favicon name="Sarah" color="bg-emerald-500/10 text-emerald-400" />
                <span className="text-[8px] text-foreground font-medium">Sarah</span>
                <Pin className="h-2.5 w-2.5 text-shield-cyan ml-auto" />
              </div>
              <p className="text-[8px] text-muted-foreground">Match for 72hrs. Campaign ready.</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  enterprise: {
    title: 'Org Overview',
    content: (
      <div className="space-y-1.5">
        {[
          { name: 'SEO Division', members: 12, signals: 18, color: 'bg-emerald-500/10 text-emerald-400' },
          { name: 'Product Intel', members: 8, signals: 12, color: 'bg-shield-cyan/10 text-shield-cyan' },
          { name: 'APAC Region', members: 6, signals: 14, color: 'bg-tivion-amber/10 text-tivion-amber' },
        ].map((d) => (
          <div key={d.name} className="flex items-center gap-2 p-1.5 rounded-lg bg-background/50 border border-border/30">
            <Favicon name={d.name} color={d.color} />
            <div className="min-w-0 flex-1">
              <span className="text-[10px] font-semibold text-foreground block leading-tight">{d.name}</span>
              <span className="text-[8px] text-muted-foreground">{d.members} members</span>
            </div>
            <span className="text-[9px] text-muted-foreground">{d.signals} signals</span>
          </div>
        ))}
        <div className="text-[9px] text-shield-cyan mt-1">26 team members &middot; 3 departments</div>
      </div>
    ),
  },
};

/* ─── Mega Menu Dropdown ─── */

function MegaMenuDropdown({ category, data, onClose }) {
  const [activePreview, setActivePreview] = useState(data.columns[0].links[0].previewId);
  const preview = previews[activePreview];
  const colCount = data.columns.length;
  const gridClass = colCount === 3
    ? 'lg:grid-cols-[2fr,2fr,2fr,3fr]'
    : colCount === 2
      ? 'lg:grid-cols-[3fr,3fr,4fr]'
      : 'lg:grid-cols-[3fr,4fr]';

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="absolute top-full left-0 right-0 z-50"
    >
      {/* Spacer to maintain hover */}
      <div className="h-2" />

      <div className={`mx-auto px-6 ${colCount === 3 ? 'max-w-[1100px]' : 'max-w-[960px]'}`}>
        <div className="rounded-xl border border-border bg-card/95 backdrop-blur-xl shadow-2xl shadow-black/20 overflow-hidden">
          <div className={`grid ${gridClass}`}>
            {/* Navigation columns */}
            {data.columns.map((col, colIdx) => (
              <div key={colIdx} className={`p-6 ${colIdx > 0 ? 'border-l border-border/20' : ''}`}>
                <div className="space-y-0.5">
                  {col.links.map((link) => (
                    <Link
                      key={link.title}
                      href={link.href}
                      onClick={onClose}
                      onMouseEnter={() => setActivePreview(link.previewId)}
                      className={`group flex items-start gap-3 p-2.5 rounded-lg transition-colors ${
                        activePreview === link.previewId
                          ? 'bg-shield-cyan/5'
                          : 'hover:bg-muted/50'
                      }`}
                    >
                      <div className={`mt-0.5 p-1.5 rounded-lg transition-colors ${
                        activePreview === link.previewId
                          ? 'bg-shield-cyan/10 text-shield-cyan'
                          : 'bg-muted/50 text-muted-foreground group-hover:text-foreground'
                      }`}>
                        <link.icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium transition-colors ${
                            activePreview === link.previewId
                              ? 'text-foreground'
                              : 'text-foreground/80 group-hover:text-foreground'
                          }`}>
                            {link.title}
                          </span>
                          {link.live && (
                            <span className="relative flex h-2 w-2">
                              <span className="absolute inline-flex h-full w-full rounded-full bg-shield-cyan animate-ping opacity-40" />
                              <span className="relative inline-flex h-2 w-2 rounded-full bg-shield-cyan" />
                            </span>
                          )}
                        </div>
                        <p className="text-[12px] text-muted-foreground leading-snug mt-0.5">
                          {link.desc}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            {/* Live Preview panel — visually anchored with darker bg */}
            <div className="border-l border-border/40 bg-muted/30 p-6 hidden lg:block">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-shield-cyan mb-3">
                Intelligence Preview
              </div>
              <AnimatePresence mode="wait">
                {preview && (
                  <motion.div
                    key={activePreview}
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="text-xs font-semibold text-foreground mb-2">{preview.title}</div>
                    {preview.content}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Mobile Accordion Category ─── */

function MobileCategory({ data, onClose }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-border/30 last:border-0">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full py-3 text-sm font-medium text-foreground"
      >
        {data.label}
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pb-3 space-y-1">
              {data.columns.flatMap((col) => col.links).map((link) => (
                <Link
                  key={link.title}
                  href={link.href}
                  onClick={onClose}
                  className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="mt-0.5 p-1 rounded bg-muted/50 text-muted-foreground">
                    <link.icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground">{link.title}</span>
                      {link.live && (
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-shield-cyan animate-ping opacity-40" />
                          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-shield-cyan" />
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">{link.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Main Navigation ─── */

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const closeTimeoutRef = useRef(null);
  const user = useUser();
  const { activeOrg } = useOrg();
  const dashboardHref = activeOrg ? `/org/${activeOrg.orgId}/websites` : '/org';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mega menu when scrolling
  useEffect(() => {
    if (!activeMenu) return;
    const handleScroll = () => setActiveMenu(null);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeMenu]);

  const handleMenuEnter = (key) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveMenu(key);
  };

  const handleMenuLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setActiveMenu(null), 150);
  };

  const handleDropdownEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const closeMobile = () => setIsMobileMenuOpen(false);

  const simpleLinks = [
    { href: '/integrations', label: 'Integrations' },
    { href: '/pricing', label: 'Pricing' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || activeMenu
          ? 'bg-background/95 backdrop-blur-md border-b border-border'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <div className="relative h-8 w-8">
              <div className="absolute inset-0 rounded-full bg-shield-cyan/20 animate-pulse-glow" />
              <img src="/images/logos/tivion-logo.svg" alt="" className="relative h-8 w-8" />
            </div>
            <span className="text-xl font-semibold tracking-wide text-foreground">Tivion</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Mega menu triggers */}
            {Object.entries(megaMenuData).map(([key, data]) => (
              <div
                key={key}
                onMouseEnter={() => handleMenuEnter(key)}
                onMouseLeave={handleMenuLeave}
              >
                <button
                  type="button"
                  className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                    activeMenu === key
                      ? 'text-foreground bg-muted/50'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {data.label}
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${activeMenu === key ? 'rotate-180' : ''}`} />
                </button>
              </div>
            ))}

            {/* Simple links */}
            {simpleLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-lg"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-4">
            {user.id ? (
              <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground">
                <Link href={dashboardHref}>{user.email}</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" className="text-muted-foreground hover:text-foreground">
                  <Link href="/login">Sign In</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-shield-cyan text-shield-cyan hover:bg-shield-cyan/10 hover:text-shield-cyan bg-transparent"
                >
                  <Link href="/onboarding/identity">Sign Up</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="md:hidden p-2 text-muted-foreground hover:text-foreground"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mega Menu Dropdown */}
      <AnimatePresence>
        {activeMenu && megaMenuData[activeMenu] && (
          <div
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleMenuLeave}
          >
            <MegaMenuDropdown
              category={activeMenu}
              data={megaMenuData[activeMenu]}
              onClose={() => setActiveMenu(null)}
            />
          </div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden overflow-hidden border-t border-border bg-background/95 backdrop-blur-md"
          >
            <div className="px-6 py-4">
              {/* Accordion categories */}
              {Object.entries(megaMenuData).map(([key, data]) => (
                <MobileCategory key={key} data={data} onClose={closeMobile} />
              ))}

              {/* Simple links */}
              {simpleLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="block py-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  onClick={closeMobile}
                >
                  {link.label}
                </Link>
              ))}

              {/* CTAs */}
              <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-border">
                {user.id ? (
                  <Button asChild variant="ghost" className="justify-start text-muted-foreground">
                    <Link href={dashboardHref}>{user.email}</Link>
                  </Button>
                ) : (
                  <>
                    <Button asChild variant="ghost" className="justify-start text-muted-foreground">
                      <Link href="/login">Sign In</Link>
                    </Button>
                    <Button
                      asChild
                      className="bg-shield-cyan text-primary-foreground hover:bg-shield-cyan/90"
                    >
                      <Link href="/onboarding/identity">Sign Up</Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
